// frontend/src/pages/PublicP2PProfile.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import p2pAxios from '../p2pAxios';
import {
  FaUserCircle,
  FaStar,
  FaThumbsUp,
  FaThumbsDown,
  FaUserClock,
  FaCheckCircle,
  FaShieldAlt,
  FaUniversity,
} from 'react-icons/fa';

/* -------------------------------------------------------------
   Helpers
------------------------------------------------------------- */

const COINS = ['USDT', 'USDC', 'BTC', 'ETH'];

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const r2 = (n) => +Number(n || 0).toFixed(2);

const safeToArray = (v) => (Array.isArray(v) ? v : (v ? [v] : []));
const isActiveAd = (ad) => {
  if (!ad) return false;
  if (ad.status) return String(ad.status).toLowerCase() === 'active';
  if (typeof ad.active === 'boolean') return ad.active;
  return !!(ad.price && ad.minLimit && ad.maxLimit && !ad.archived);
};

const fmtINR = (n) =>
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const toNum = (v) => {
  if (v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Try multiple GET endpoints until one returns a valid shape. */
async function tryFirstJson(urls, { validate, map } = {}) {
  for (const u of urls) {
    try {
      const res = await p2pAxios.get(u);
      const data = res?.data;
      if (!validate || validate(data)) {
        return map ? map(data) : data;
      }
    } catch (_) {
      // try next
    }
  }
  return null;
}

/** Extract a forgiving reputation object from profile (optional fields). */
function extractReputation(profile) {
  const rep = profile?.reputation || profile?.p2pReputation || profile?.rep || {};
  const stats = profile?.stats || profile?.p2pStats || {};

  const totalFeedback =
    toNum(rep.total) ??
    toNum(profile?.feedbackTotal) ??
    toNum(profile?.reputationTotal) ??
    null;

  const positive =
    toNum(rep.positive) ??
    toNum(profile?.positiveFeedback) ??
    toNum(profile?.feedbackPositive) ??
    null;

  const negative =
    toNum(rep.negative) ??
    toNum(profile?.negativeFeedback) ??
    toNum(profile?.feedbackNegative) ??
    null;

  const neutral =
    toNum(rep.neutral) ??
    toNum(profile?.neutralFeedback) ??
    toNum(profile?.feedbackNeutral) ??
    null;

  const totalForRate =
    totalFeedback ??
    (positive !== null || negative !== null || neutral !== null
      ? (positive || 0) + (negative || 0) + (neutral || 0)
      : null);

  const positiveRate =
    totalForRate && positive !== null
      ? Math.max(0, Math.min(100, Math.round((positive / totalForRate) * 100)))
      : null;

  const totalTrades =
    toNum(stats.totalTrades) ??
    toNum(profile?.totalTrades) ??
    toNum(profile?.tradeCount) ??
    null;

  const completedTrades =
    toNum(stats.completedTrades) ??
    toNum(profile?.completedTrades) ??
    toNum(profile?.tradesCompleted) ??
    null;

  const completionRate =
    toNum(stats.completionRate) ??
    toNum(profile?.completionRate) ??
    null;

  const isKycVerified = Boolean(
    profile?.isKycVerified ?? profile?.kycVerified ?? profile?.kyc?.status === 'verified'
  );
  const isBankVerified = Boolean(profile?.isBankVerified ?? profile?.bankVerified ?? profile?.bank?.verified);

  const externalVerified = Boolean(rep.externalVerified ?? profile?.externalVerified ?? false);

  return {
    totalFeedback,
    positiveRate,
    positive,
    neutral,
    negative,
    totalTrades,
    completedTrades,
    completionRate,
    isKycVerified,
    isBankVerified,
    externalVerified,
  };
}

function Badge({ children, title, tone = 'gray' }) {
  const toneMap = {
    gray: 'bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-neutral-700',
    green: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-900/60',
    blue: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900/60',
    indigo: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-900/60',
    amber: 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-900/60',
    red: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-900/60',
  };

  return (
    <span
      title={title || ''}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] sm:text-xs font-medium border ${toneMap[tone] || toneMap.gray}`}
    >
      {children}
    </span>
  );
}

/* -------------------------------------------------------------
   Component
------------------------------------------------------------- */

const PublicP2PProfile = () => {
  const { username } = useParams();
  const navigate = useNavigate();

  // profile
  const [profile, setProfile] = useState(null);

  // ✅ Base reputation from backend public API: { total, internalTotal, externalTotal }
  const repBase = profile?.reputation || { total: 0, internalTotal: 0, externalTotal: 0 };

  // ✅ Joined text from backend public API
  const joinedText = profile?.joined || '-';

  // ✅ Optional stats if backend sends them (safe)
  const repStats = useMemo(() => extractReputation(profile), [profile]);

  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileErr, setProfileErr] = useState('');

  // feedback (received)
  const [feedbacks, setFeedbacks] = useState([]);
  const [loadingFb, setLoadingFb] = useState(true);
  // backend summary (positive/neutral/negative)
  const [fbSummary, setFbSummary] = useState({ positive: 0, neutral: 0, negative: 0 });

  // ads (active offers)
  const [ads, setAds] = useState([]);
  const [loadingAds, setLoadingAds] = useState(true);

  // trade UI state (inline per-ad)
  const [expandedAdId, setExpandedAdId] = useState(null);
  const [amountInr, setAmountInr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // fetch profile
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingProfile(true);
      setProfileErr('');
      try {
        const res = await p2pAxios.get(`/user/public/${encodeURIComponent(username)}`);
        if (mounted) setProfile(res?.data || null);
      } catch (e) {
        if (mounted) {
          setProfile(null);
          setProfileErr(e?.response?.data?.error || e?.response?.data?.message || 'Unable to load profile.');
        }
      } finally {
        if (mounted) setLoadingProfile(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [username]);

  const userIdCandidates = useMemo(() => {
    const idish = [];
    if (profile?._id) idish.push(profile._id);
    if (profile?.id && profile.id !== profile?._id) idish.push(profile.id);
    if (profile?.userId && !idish.includes(profile.userId)) idish.push(profile.userId);
    if (profile?.user && typeof profile.user === 'string' && !idish.includes(profile.user)) idish.push(profile.user);
    if (profile?.user?._id && !idish.includes(profile.user._id)) idish.push(profile.user._id);
    return idish.filter(Boolean);
  }, [profile]);

  /* ---------------------- Feedback Received (list + summary) ---------------------- */
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingFb(true);

      const uname = encodeURIComponent(username);
      const userId = userIdCandidates[0];

      // Prefer username endpoint
      try {
        const r1 = await p2pAxios.get(`/feedback/received/by-username/${uname}`);
        if (mounted && (r1?.data?.ok || Array.isArray(r1?.data?.feedbacks))) {
          const list = r1.data.feedbacks || [];
          setFeedbacks(list);

          // if summary is not present, compute from list
          const s = r1.data.summary || computeSummaryFromList(list);
          setFbSummary(s);

          setLoadingFb(false);
          return;
        }
      } catch (_) {}

      // Fallback to userId endpoint
      if (userId) {
        try {
          const r2 = await p2pAxios.get(`/feedback/received/${userId}`);
          if (mounted && (r2?.data?.ok || Array.isArray(r2?.data?.feedbacks))) {
            const list = r2.data.feedbacks || [];
            setFeedbacks(list);

            const s = r2.data.summary || computeSummaryFromList(list);
            setFbSummary(s);

            setLoadingFb(false);
            return;
          }
        } catch (_) {}
      }

      // If neither worked, attempt generic patterns
      const fb = await tryFirstJson(
        [
          userId && `/feedback/user/${userId}`,
          userId && `/feedback/of/${userId}`,
          `/feedback/user/by-username/${uname}`,
          `/feedback/of/${uname}`,
          `/feedback/list?username=${uname}`,
          userId && `/feedback/list?userId=${userId}`,
        ].filter(Boolean),
        {
          validate: (d) => Array.isArray(d?.feedbacks) || Array.isArray(d?.data) || Array.isArray(d),
          map: (d) => d?.feedbacks || d?.data || (Array.isArray(d) ? d : []),
        }
      );

      if (mounted) {
        const list = fb || [];
        setFeedbacks(list);
        setFbSummary(computeSummaryFromList(list));
        setLoadingFb(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [username, userIdCandidates]);

  function computeSummaryFromList(list) {
    const s = { positive: 0, neutral: 0, negative: 0 };
    for (const f of list || []) {
      const t = String(f?.type || f?.rating || '').toLowerCase();
      if (t === 'positive' || t === 'pos' || t === 'good') s.positive += 1;
      else if (t === 'negative' || t === 'neg' || t === 'bad') s.negative += 1;
      else s.neutral += 1;
    }
    return s;
  }

  /* ---------------------- Active Offers (Ads) ---------------------- */
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingAds(true);
      const uname = encodeURIComponent(username);
      const id = userIdCandidates[0];

      // 1) Try user-scoped ad endpoints first
      const byUser = await tryFirstJson(
        [
          id && `/ads/by-user/${id}`,
          `/ads/list/user?username=${uname}`,
          id && `/ads/list/user?userId=${id}`,
          `/ads/search?username=${uname}`,
          `/ads/search?user=${uname}`,
        ].filter(Boolean),
        {
          validate: (d) => Array.isArray(d?.ads) || Array.isArray(d?.data) || Array.isArray(d),
          map: (d) => d?.ads || d?.data || (Array.isArray(d) ? d : []),
        }
      );

      if (byUser && mounted) {
        setAds(byUser.filter(isActiveAd));
        setLoadingAds(false);
        return;
      }

      // 2) Fallback: client-side filter using the same endpoints as Market
      try {
        const collected = [];
        for (const type of ['buy', 'sell']) {
          for (const coin of COINS) {
            try {
              const res = await p2pAxios.get(`/ads/list/${type}?coin=${coin}`);
              const list = res?.data?.ads || [];
              for (const ad of list) {
                const u = ad?.user || {};
                const matchByName = u?.username && String(u.username).toLowerCase() === String(username).toLowerCase();
                const matchById =
                  userIdCandidates.length && (u?._id === userIdCandidates[0] || u?.id === userIdCandidates[0]);
                if ((matchByName || matchById) && isActiveAd(ad)) {
                  collected.push(ad);
                }
              }
            } catch (_) {
              /* continue */
            }
          }
        }
        if (mounted) setAds(collected);
      } finally {
        if (mounted) setLoadingAds(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [username, userIdCandidates]);

  /* ---------------------- Start Trade from Profile ---------------------- */
  const toggleExpand = (adId) => {
    setExpandedAdId((prev) => (prev === adId ? null : adId));
    setAmountInr('');
  };

  const quickAmount = (type, ad) => {
    if (!ad) return;
    const { minLimit, maxLimit } = ad;
    let v = minLimit;
    if (type === '25') v = maxLimit * 0.25;
    if (type === '50') v = maxLimit * 0.5;
    if (type === '75') v = maxLimit * 0.75;
    if (type === '100') v = maxLimit;
    v = clamp(v, minLimit, maxLimit);
    setAmountInr(String(r2(v)));
  };

  const startTrade = async (ad) => {
    const fiat = parseFloat(amountInr);
    if (isNaN(fiat) || fiat <= 0) return alert('Please enter a valid INR amount.');
    if (fiat < ad.minLimit || fiat > ad.maxLimit) {
      return alert(`Amount must be between ₹${fmtINR(ad.minLimit)} and ₹${fmtINR(ad.maxLimit)}.`);
    }

    setSubmitting(true);
    try {
      const chosenMethod = (Array.isArray(ad.paymentMethods) && ad.paymentMethods[0]) || 'bank';

      const res = await p2pAxios.post('/trades/start', {
        adId: ad._id,
        fiatAmount: fiat,
        paymentMethod: chosenMethod,
      });

      if (res.data?.success && res.data?.trade?._id) {
        navigate(`/p2p/trade/${res.data.trade._id}`);
      } else {
        alert(res.data?.message || 'Failed to start trade.');
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) alert('Please log in to start a trade.');
      else alert(err?.response?.data?.message || err?.response?.data?.error || 'Error starting trade');
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------------------- Derived (combined internal + verified external) ---------------------- */
  const externalVerifiedCounts = useMemo(() => {
    const rows = Array.isArray(profile?.externalReputation) ? profile.externalReputation : [];
    const verified = rows.filter((r) => !!r?.verified);

    return verified.reduce(
      (acc, r) => {
        acc.positive += Number(r?.positive || 0);
        acc.neutral += Number(r?.neutral || 0);
        acc.negative += Number(r?.negative || 0);
        acc.total += Number(r?.feedbackTotal || 0);
        return acc;
      },
      { positive: 0, neutral: 0, negative: 0, total: 0 }
    );
  }, [profile]);

  const combined = useMemo(() => {
    const internal = {
      positive: Number(fbSummary?.positive || 0),
      neutral: Number(fbSummary?.neutral || 0),
      negative: Number(fbSummary?.negative || 0),
    };

    return {
      positive: internal.positive + externalVerifiedCounts.positive,
      neutral: internal.neutral + externalVerifiedCounts.neutral,
      negative: internal.negative + externalVerifiedCounts.negative,
      // ✅ total should come from backend combined reputation (internal + verified external)
      total: Number(repBase.total || 0),
    };
  }, [fbSummary, externalVerifiedCounts, repBase]);

  const positive = combined.positive;
  const neutral = combined.neutral;
  const negative = combined.negative;
  const totalFb = combined.total;

  // ✅ define posRate (fix ESLint + show correct %)
  const posRate = totalFb > 0 ? Math.round((positive / totalFb) * 100) : null;

  /* ---------------------- UI ---------------------- */
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 dark:from-neutral-950 dark:to-neutral-900 transition-colors"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Back link */}
        <div className="mb-3 sm:mb-4">
          <Link
            to="/p2p"
            className="inline-flex items-center text-blue-700 dark:text-blue-400 hover:underline text-sm sm:text-base"
          >
            ← Back to P2P Hub
          </Link>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-lg border border-blue-100 dark:border-neutral-800 sm:border-l-8 sm:border-t border-blue-600/80 overflow-hidden">
          {/* Header / Identity */}
          <div className="p-4 sm:p-6 md:p-8">
            {loadingProfile ? (
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-blue-100 dark:bg-neutral-800 animate-pulse" />
                <div className="flex-1">
                  <div className="h-5 sm:h-6 w-40 bg-blue-100 dark:bg-neutral-800 rounded animate-pulse mb-2" />
                  <div className="h-4 w-64 bg-blue-100 dark:bg-neutral-800 rounded animate-pulse" />
                </div>
              </div>
            ) : profile ? (
              <div className="flex items-start sm:items-center gap-4 sm:gap-6">
                <FaUserCircle className="text-5xl sm:text-6xl text-blue-500 dark:text-blue-400 shrink-0" />
                <div className="min-w-0 w-full">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-2xl sm:text-3xl font-bold text-blue-900 dark:text-blue-200 truncate">
                      @{profile.username}
                    </h1>

                    {/* Total feedback (combined internal + verified external) */}
                    {repBase?.total !== null && repBase?.total !== undefined && (
                      <Badge tone="indigo" title="Total feedback (internal + verified external)">
                        <FaStar /> {Number(repBase.total || 0).toLocaleString('en-IN')}
                      </Badge>
                    )}

                    {/* Positive rate (computed from combined counts) */}
                    {posRate !== null && (
                      <Badge tone="green" title="Positive feedback rate (internal + verified external)">
                        <FaThumbsUp /> {posRate}%
                      </Badge>
                    )}

                    {/* Trade stats only if backend provides them */}
                    {(repStats?.completedTrades != null || repStats?.totalTrades != null) && (
                      <Badge tone="blue" title="Trade stats">
                        🧾 {repStats.completedTrades ?? 0}/{repStats.totalTrades ?? 0}
                      </Badge>
                    )}

                    {repStats?.completionRate != null && (
                      <Badge tone="amber" title="Completion rate">
                        <FaCheckCircle /> {repStats.completionRate}%
                      </Badge>
                    )}

                    {repStats?.isKycVerified && (
                      <Badge tone="blue" title="KYC verified">
                        <FaShieldAlt /> KYC
                      </Badge>
                    )}

                    {repStats?.isBankVerified && (
                      <Badge tone="indigo" title="Bank verified">
                        <FaUniversity /> Bank
                      </Badge>
                    )}

                    {/* External verified badge if externalTotal > 0 */}
                    {Number(repBase?.externalTotal || 0) > 0 && (
                      <Badge tone="amber" title="Verified external reputation included">
                        🌐 External
                      </Badge>
                    )}
                  </div>

                  <p className="mt-1 text-sm sm:text-base text-gray-700 dark:text-gray-300 break-words">
                    {profile.bio?.trim() || 'No bio provided.'}
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center text-red-600 dark:text-red-400 font-semibold">
                {profileErr || 'Profile not found'}
              </div>
            )}
          </div>

          {/* Body */}
          <div className="p-4 sm:p-6 md:p-8">
            {/* Trust & Feedback summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="rounded-xl border border-blue-100 dark:border-neutral-800 p-4 sm:p-5 bg-white dark:bg-neutral-900">
                <h2 className="text-lg sm:text-xl font-semibold text-blue-800 dark:text-blue-300 mb-3">
                  🕒 Trust & Activity
                </h2>
                <ul className="text-sm sm:text-base text-gray-800 dark:text-gray-200 space-y-2">
                  <li className="flex items-center gap-2">
                    <FaUserClock className="text-blue-600 dark:text-blue-400" />
                    <span>
                      <span className="text-gray-600 dark:text-gray-400">Joined:</span>{' '}
                      <span className="font-medium">{joinedText}</span>
                    </span>
                  </li>
                  <li className="flex items-center gap-2">
                    <FaStar className="text-yellow-500" />
                    <span>
                      <span className="text-gray-600 dark:text-gray-400">Trusted by</span>{' '}
                      <span className="font-medium">{(profile?.trustedBy ?? 0).toLocaleString('en-IN')}</span>{' '}
                      <span className="text-gray-600 dark:text-gray-400">users</span>
                    </span>
                  </li>
                </ul>
              </div>

              <div className="rounded-xl border border-blue-100 dark:border-neutral-800 p-4 sm:p-5 bg-white dark:bg-neutral-900">
                <h2 className="text-lg sm:text-xl font-semibold text-blue-800 dark:text-blue-300 mb-3">
                  💬 Feedback Summary
                </h2>
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <StatCard
                    icon={<FaThumbsUp />}
                    label="Positive"
                    value={positive}
                    light="bg-green-100 text-green-900"
                    dark="dark:bg-green-900/30 dark:text-green-300"
                  />
                  <StatCard
                    icon={<FaStar />}
                    label="Neutral"
                    value={neutral}
                    light="bg-blue-100 text-blue-900"
                    dark="dark:bg-blue-900/30 dark:text-blue-300"
                  />
                  <StatCard
                    icon={<FaThumbsDown />}
                    label="Negative"
                    value={negative}
                    light="bg-red-100 text-red-900"
                    dark="dark:bg-red-900/30 dark:text-red-300"
                  />
                </div>

                <div className="mt-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  Total feedback:{' '}
                  <span className="font-semibold text-gray-900 dark:text-gray-100">{Number(totalFb || 0).toLocaleString('en-IN')}</span>
                  {posRate !== null && (
                    <>
                      {' · '}Positive rate:{' '}
                      <span className="font-semibold text-green-700 dark:text-green-300">{posRate}%</span>
                    </>
                  )}
                </div>

                {/* Optional breakdown line */}
                <div className="mt-2 text-[11px] sm:text-xs text-gray-500 dark:text-gray-400">
                  Internal: <span className="font-medium">{Number(repBase?.internalTotal || 0).toLocaleString('en-IN')}</span>
                  {' · '}
                  External (verified): <span className="font-medium">{Number(repBase?.externalTotal || 0).toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>

            {/* Active Offers (with inline "Trade" launcher) */}
            <div className="mt-8">
              <h2 className="text-lg sm:text-xl font-semibold text-blue-800 dark:text-blue-300 mb-3">
                📌 Active Offers
              </h2>

              {loadingAds ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="rounded-2xl border border-blue-100 dark:border-neutral-800 p-4 bg-white dark:bg-neutral-900"
                    >
                      <div className="h-4 w-24 bg-blue-100 dark:bg-neutral-800 rounded animate-pulse mb-2" />
                      <div className="h-4 w-40 bg-blue-100 dark:bg-neutral-800 rounded animate-pulse mb-2" />
                      <div className="h-4 w-32 bg-blue-100 dark:bg-neutral-800 rounded animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : ads.length === 0 ? (
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">No active offers.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                  {ads.slice(0, 12).map((ad) => {
                    const pm = safeToArray(ad.paymentMethods).join(', ') || '—';
                    const symbol = String(ad.coin || 'USDT').toUpperCase();
                    const chip = String(ad.type || '').toUpperCase() === 'SELL' ? 'SELL' : 'BUY';
                    const isExpanded = expandedAdId === ad._id;

                    return (
                      <div
                        key={ad._id}
                        className="rounded-2xl border border-blue-100 dark:border-neutral-800 p-4 bg-white dark:bg-neutral-900"
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <span
                            className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${
                              chip === 'SELL'
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            }`}
                          >
                            {chip}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{symbol}</span>
                        </div>

                        <div className="text-sm sm:text-base text-gray-800 dark:text-gray-200 space-y-0.5">
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Price:</span>{' '}
                            <span className="font-semibold">₹{fmtINR(ad.price)}</span> / {symbol}
                          </div>
                          <div className="tabular-nums">
                            <span className="text-gray-600 dark:text-gray-400">Limits:</span>{' '}
                            ₹{fmtINR(ad.minLimit)} – ₹{fmtINR(ad.maxLimit)}
                          </div>
                          <div>
                            <span className="text-gray-600 dark:text-gray-400">Payment:</span> {pm}
                          </div>
                          {ad?.expireMinutes && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Time limit: {ad.expireMinutes} min
                            </div>
                          )}
                        </div>

                        <div className="mt-3 flex justify-end">
                          <button
                            type="button"
                            onClick={() => toggleExpand(ad._id)}
                            className="bg-indigo-600 text-white px-3 py-1.5 rounded-md text-xs sm:text-sm hover:bg-indigo-700 transition"
                            aria-expanded={isExpanded}
                          >
                            {isExpanded ? 'Close' : 'Trade'}
                          </button>
                        </div>

                        {isExpanded && (
                          <div className="mt-3 border-t border-gray-200 dark:border-neutral-800 pt-3">
                            <label className="block text-[11px] sm:text-xs font-medium text-gray-700 dark:text-gray-200 mb-1">
                              Enter amount (INR)
                            </label>
                            <input
                              type="number"
                              min={ad.minLimit}
                              max={ad.maxLimit}
                              value={amountInr}
                              onChange={(e) => setAmountInr(e.target.value)}
                              placeholder={`₹ between ${fmtINR(ad.minLimit)} and ${fmtINR(ad.maxLimit)}`}
                              className="w-full border border-gray-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 rounded-md px-3 py-2 text-[12px] sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              <QuickChip onClick={() => setAmountInr(String(r2(ad.minLimit)))}>Min</QuickChip>
                              <QuickChip onClick={() => quickAmount('25', ad)}>25%</QuickChip>
                              <QuickChip onClick={() => quickAmount('50', ad)}>50%</QuickChip>
                              <QuickChip onClick={() => quickAmount('75', ad)}>75%</QuickChip>
                              <QuickChip onClick={() => quickAmount('100', ad)}>Max</QuickChip>
                            </div>

                            <div className="mt-3 flex gap-2">
                              <button
                                type="button"
                                disabled={submitting}
                                onClick={() => startTrade(ad)}
                                className="bg-green-600 text-white px-4 py-2 rounded-md text-xs sm:text-sm hover:bg-green-700 transition disabled:opacity-60"
                              >
                                {submitting ? 'Starting…' : 'Start Trade'}
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleExpand(ad._id)}
                                className="bg-gray-200 dark:bg-neutral-800 text-gray-800 dark:text-gray-100 px-4 py-2 rounded-md text-xs sm:text-sm hover:bg-gray-300 dark:hover:bg-neutral-700 transition"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Feedback Received List */}
            <div className="mt-8">
              <h2 className="text-lg sm:text-xl font-semibold text-blue-800 dark:text-blue-300 mb-3">
                📥 Feedback Received
              </h2>

              {loadingFb ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="rounded-xl border border-blue-100 dark:border-neutral-800 p-4 bg-white dark:bg-neutral-900"
                    >
                      <div className="h-4 w-20 bg-blue-100 dark:bg-neutral-800 rounded animate-pulse mb-2" />
                      <div className="h-4 w-48 bg-blue-100 dark:bg-neutral-800 rounded animate-pulse mb-1.5" />
                      <div className="h-3 w-24 bg-blue-100 dark:bg-neutral-800 rounded animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : feedbacks.length === 0 ? (
                <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">No feedback yet.</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {feedbacks.slice(0, 10).map((f, idx) => {
                    const t = String(f?.type || f?.rating || '').toLowerCase();
                    const badge = t === 'positive' ? '👍 Positive' : t === 'negative' ? '👎 Negative' : '😐 Neutral';

                    const tone = t === 'positive' ? 'green' : t === 'negative' ? 'red' : 'gray';

                    const fromUserObj = f?.from || f?.fromUser;
                    const fromUsername =
                      (typeof fromUserObj === 'object' && (fromUserObj?.username || fromUserObj?.name)) ||
                      (typeof f?.fromUser === 'string' ? f?.fromUser : '') ||
                      (typeof f?.from === 'string' ? f?.from : '') ||
                      '';

                    const when = f?.createdAt || f?.updatedAt;
                    const whenText = when ? new Date(when).toLocaleString('en-IN') : '';

                    const tradeId = f?.trade || f?.tradeId;

                    return (
                      <div
                        key={idx}
                        className="rounded-xl border border-blue-100 dark:border-neutral-800 p-4 bg-white dark:bg-neutral-900"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <Badge tone={tone} title="Feedback type">
                            {badge}
                          </Badge>
                          {whenText && (
                            <span className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400">{whenText}</span>
                          )}
                        </div>

                        {f?.comment ? (
                          <p className="text-sm text-gray-800 dark:text-gray-200">{f.comment}</p>
                        ) : (
                          <p className="text-sm text-gray-600 dark:text-gray-400 italic">No comment.</p>
                        )}

                        <div className="mt-2 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          {fromUsername ? (
                            <>
                              From{' '}
                              <Link
                                to={`/p2p/user/${encodeURIComponent(fromUsername)}`}
                                className="font-medium text-blue-700 dark:text-blue-300 hover:underline"
                              >
                                @{fromUsername}
                              </Link>
                            </>
                          ) : (
                            <>
                              From <span className="font-medium">@user</span>
                            </>
                          )}

                          {tradeId && (
                            <>
                              {' · '}
                              <Link to={`/p2p/trade/${tradeId}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                                View trade
                              </Link>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

/* ---------- Tiny UI bits ---------- */
function StatCard({ icon, label, value, light, dark }) {
  return (
    <div className={`flex flex-col items-start sm:items-center gap-1 rounded-lg p-3 sm:p-4 shadow-sm ${light} ${dark}`}>
      <div className="flex items-center gap-2 text-sm sm:text-base font-semibold">
        <span className="text-base sm:text-lg">{icon}</span>
        <span>{label}</span>
      </div>
      <div className="text-2xl sm:text-3xl font-bold tabular-nums">{Number(value || 0).toLocaleString('en-IN')}</div>
    </div>
  );
}

function QuickChip({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-2 py-0.5 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-neutral-800 dark:hover:bg-neutral-700 text-[10px] sm:text-xs text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-neutral-700 transition"
    >
      {children}
    </button>
  );
}

export default PublicP2PProfile;
